import { BRAND } from "constants/brand";
import { Ring } from "@uiball/loaders";
import "assets/styles/table.scss";
import { useState, useRef } from "react";
import { Toast } from "primereact/toast";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import {
    useComplaints,
    useMarkComplaintSeen,
    useMarkAllComplaintsSeen,
    useDeleteComplaint,
} from "hooks/useComplaints";
import Complain from "./complainItem";
import SendNotification from "./SendNotification/SendNotification";

const Complains = () => {
    const [showDelete, setShowDelete] = useState(false);
    const [showSendNotification, setSendNotification] = useState(false);
    const [selectedComplain, setSelectedComplain] = useState(null);
    const toast = useRef(null);

    /**
     * OFF FIRESTORE (api#136), AND ON REACT QUERY.
     *
     * This screen used to bypass the API entirely: an `onSnapshot` listener on
     * the whole `complains` collection, and a `writeBatch` that marked EVERY
     * unseen complaint read the moment the page loaded. Three things came with
     * that, and all three are fixed by reading the API instead:
     *
     *  1. **No city scoping was possible.** The rule for who may see a
     *     complaint lives in the API, not in Firestore rules, so a city admin
     *     read every city's complaints — names and phones included. The batch
     *     had no city filter either, so opening the page also cleared other
     *     cities' badges.
     *  2. **A listener on a whole collection bills reads for as long as the tab
     *     is open**, on a quota this project has already exhausted once.
     *  3. **Two `seen` flags.** The batch wrote Firestore's; the API owned a
     *     column that meant nothing.
     *
     * OPENING THE PAGE NO LONGER MARKS ANYTHING READ. That made the sidebar
     * badge meaningless: the queue emptied itself on every visit whether or not
     * anybody had dealt with a complaint. Marking read is the operator's
     * action now, one at a time or all at once.
     *
     * The list, the badge and every write go through react-query, so a mark or
     * a delete moves both at once — see hooks/useComplaints.js.
     */
    const { complaints, loading } = useComplaints();
    const markSeen = useMarkComplaintSeen();
    const markAllSeen = useMarkAllComplaintsSeen();
    const deleteComplaint = useDeleteComplaint();

    const unreadCount = complaints.filter((row) => !row.seen).length;

    const showMessage = (type, head, content) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 3000,
        });
    };

    const toggleDelete = () => {
        setShowDelete((pre) => !pre);
    };

    const toggleNotification = () => {
        setSendNotification((pre) => !pre);
    };

    const setSelectedComplainValue = (complain) => {
        setSelectedComplain(complain);
    };

    const handleMarkSeen = async (complain) => {
        try {
            await markSeen.mutateAsync(complain.id);
        } catch {
            showMessage("error", "هناك خطأ", "تعذر تحديث الشكوى");
        }
    };

    const handleMarkAllSeen = async () => {
        try {
            await markAllSeen.mutateAsync();
            showMessage("success", "تم", "لم تعد هناك شكاوى جديدة");
        } catch {
            showMessage("error", "هناك خطأ", "تعذر تحديث الشكاوى");
        }
    };

    const confirmDelete = async () => {
        try {
            await deleteComplaint.mutateAsync(selectedComplain.id);
            showMessage("warn", "تم الحذف", "تم حذف الشكوى بنجاح");
            toggleDelete();
        } catch {
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    return (
        <div className="items complains-page">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />
            <div className="complains-page__head">
                <h2 style={{ margin: 0, color: BRAND }}>الشكاوى</h2>
                {unreadCount ? (
                    <button className="complains-page__mark-all" onClick={handleMarkAllSeen}>
                        قراءة الكل ({unreadCount})
                    </button>
                ) : null}
            </div>
            {loading ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <div className="complains-page__list">
                        {complaints.length ? (
                            complaints.map((complain) => (
                                <Complain
                                    complain={complain}
                                    toggleDelete={toggleDelete}
                                    setSelectedComplainValue={setSelectedComplainValue}
                                    toggleNotification={toggleNotification}
                                    onMarkSeen={handleMarkSeen}
                                    key={complain.id}
                                />
                            ))
                        ) : (
                            <div className="surface-card surface-card--dashed complains-page__empty">
                                لا توجد شكاوى
                            </div>
                        )}
                    </div>
                    {/* <Stack spacing={1}>
            <Pagination
              count={pages}
              page={page}
              shape="rounded"
              color={BRAND}
              style={{ margin: "20px auto 0" }}
              onChange={handleChangePage}
            />
          </Stack> */}
                    <SendNotification
                        show={showSendNotification}
                        close={toggleNotification}
                        userId={selectedComplain ? selectedComplain.userId : null}
                        showMessage={showMessage}
                    />
                    <DeleteItem
                        show={showDelete}
                        close={toggleDelete}
                        loading={deleteComplaint.isPending}
                        confirm={confirmDelete}
                    />
                </>
            )}
        </div>
    );
};
export default Complains;
